import { Navigate } from '@tanstack/react-router'
import type { JurisdictionStatus, JurisdictionView } from '@tex/finance-shared'
import { type FormEvent, useState } from 'react'
import { Alert, Button, Card, Field, Input, Select, Textarea } from '../components/ui'
import { errorMessage } from '../lib/api'
import { US_STATES } from '../lib/case-form'
import { formatDate, formatDay } from '../lib/format'
import { useJurisdictions, usePutJurisdiction } from '../lib/programs'
import { isTexStaff, useMe } from '../lib/session'

const jurisdictionTone: Record<JurisdictionStatus | 'missing', string> = {
  approved: 'bg-green-100 text-green-900',
  pending: 'bg-amber-100 text-amber-900',
  blocked: 'bg-red-100 text-red-900',
  missing: 'bg-neutral-100 text-neutral-500',
}

export function JurisdictionBadge({ status }: { status: JurisdictionStatus | 'missing' }) {
  return <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${jurisdictionTone[status]}`}>{status}</span>
}

const STATUSES: JurisdictionStatus[] = ['approved', 'pending', 'blocked']

export function JurisdictionsPage() {
  const me = useMe()
  const jurisdictions = useJurisdictions()
  const [editing, setEditing] = useState<string | null>(null)

  if (me.data === null) return <Navigate to="/sign-in" />
  if (me.data && !isTexStaff(me.data)) return <Alert tone="error">Only Tex staff can see operating jurisdictions.</Alert>

  const byState = new Map((jurisdictions.data?.jurisdictions ?? []).map((j) => [j.state, j]))
  const isOps = me.data?.tex_role === 'ops_admin'
  const counts = { approved: 0, pending: 0, blocked: 0, missing: 0 }
  for (const st of US_STATES) counts[byState.get(st)?.status ?? 'missing'] += 1

  return (
    <section className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-neutral-900">Operating jurisdictions</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Tex does not broker in states that require a commercial-loan broker licence or registration. <strong>Blocked</strong> states make a
          case ineligible on the borrower state and, separately, the equipment use state. <strong>Approved</strong> is set only once counsel has
          cleared a state; everything else is <strong>pending</strong> and routes to manual review. A state with no row counts as pending.
        </p>
        <p className="mt-2 text-xs text-neutral-500">
          {counts.approved} approved · {counts.pending} pending · {counts.blocked} blocked{counts.missing ? ` · ${counts.missing} without a row` : ''}
        </p>
      </div>

      {jurisdictions.isError && <Alert tone="error">{errorMessage(jurisdictions.error)}</Alert>}
      <div className="overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="border-b border-neutral-100 bg-neutral-50/60 text-left font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="px-4 py-2">State</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Basis</th>
              <th className="px-4 py-2">Effective</th>
              <th className="px-4 py-2">Updated</th>
              {isOps && <th className="px-4 py-2" />}
            </tr>
          </thead>
          <tbody>
            {US_STATES.map((st) => {
              const j = byState.get(st)
              return (
                <tr key={st} className={`border-t border-neutral-100 ${j?.status === 'blocked' ? 'bg-red-50/40' : ''}`}>
                  <td className="px-4 py-2 font-mono font-medium">{st}</td>
                  <td className="px-4 py-2">
                    <JurisdictionBadge status={j?.status ?? 'missing'} />
                  </td>
                  <td className="max-w-md px-4 py-2 text-neutral-700">{j?.basis ?? <span className="text-neutral-400">—</span>}</td>
                  <td className="px-4 py-2 text-neutral-500">{j?.effective_from ? `${formatDay(j.effective_from)} → ${j.effective_to ? formatDay(j.effective_to) : 'open'}` : '—'}</td>
                  <td className="px-4 py-2 text-neutral-500">{j ? formatDate(j.updated_at) : '—'}</td>
                  {isOps && (
                    <td className="px-4 py-2 text-right">
                      <Button variant="secondary" className="!px-2 !py-0.5 text-xs" onClick={() => setEditing(st)}>
                        Edit
                      </Button>
                    </td>
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {isOps && editing && <EditJurisdictionCard key={editing} state={editing} current={byState.get(editing) ?? null} onDone={() => setEditing(null)} />}
    </section>
  )
}

function EditJurisdictionCard({ state, current, onDone }: { state: string; current: JurisdictionView | null; onDone: () => void }) {
  const put = usePutJurisdiction()
  const [status, setStatus] = useState<JurisdictionStatus>(current?.status ?? 'pending')
  const [basis, setBasis] = useState(current?.basis ?? '')
  const [effectiveFrom, setEffectiveFrom] = useState(current?.effective_from ?? '')
  const [effectiveTo, setEffectiveTo] = useState(current?.effective_to ?? '')

  function submit(e: FormEvent) {
    e.preventDefault()
    put.mutate(
      { state, status, basis: basis.trim(), effective_from: effectiveFrom || null, effective_to: effectiveTo || null },
      { onSuccess: onDone },
    )
  }

  return (
    <Card
      title={`Edit ${state}`}
      actions={
        <Button variant="secondary" className="!px-2 !py-0.5 text-xs" onClick={onDone}>
          Cancel
        </Button>
      }
    >
      <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
        <Field label="Status" hint="Approved only with counsel clearance on file; blocked needs the statute in the basis.">
          <Select value={status} onChange={(e) => setStatus(e.target.value as JurisdictionStatus)}>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Effective from">
            <Input type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
          </Field>
          <Field label="Effective to">
            <Input type="date" value={effectiveTo} onChange={(e) => setEffectiveTo(e.target.value)} />
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Basis" hint="Factual: the statute, the counsel memo reference, or why it is still pending. Mark secondary sources 'to verify'.">
            <Textarea required minLength={3} rows={3} value={basis} onChange={(e) => setBasis(e.target.value)} />
          </Field>
        </div>
        <div className="sm:col-span-2 flex items-center gap-3">
          <Button type="submit" disabled={put.isPending}>
            Save {state}
          </Button>
          {put.isError && <Alert tone="error">{errorMessage(put.error)}</Alert>}
        </div>
      </form>
    </Card>
  )
}
